import api from "./client";
import type { ManualTrackingStatus } from "../utils/carrierStatus";

export const shipments = {
  /** dateFrom (YYYY-MM-DD) limits every count to items created since; omitted = all time. */
  getDashboardStats: (dateFrom?: string, signal?: AbortSignal) =>
    api.get('/shipments/dashboard-stats', { params: { dateFrom }, signal }).then((res) => res.data),
  /** Rates quoted vs rate chosen, one entry per shipment on the order. */
  getRateComparison: (orderId: string) =>
    api.get(`/shipments/rate-comparison/${orderId}`).then((res) => res.data),
  /**
   * The packages of one order, for the order page's shipment section. Granted
   * by the `orders.shipments` action, not the Tracking History menu.
   */
  getOrderPackages: (orderId: string) =>
    api.get(`/shipments/order/${encodeURIComponent(orderId)}/packages`).then((res) => res.data),
  getPackagesHistory: (
    page: number = 1,
    limit: number = 10,
    filters: any = {},
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.carrier ? { carrier: filters.carrier } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.createdBy ? { createdBy: filters.createdBy } : {}),
      ...(filters.externalSystemName ? { externalSystemName: filters.externalSystemName } : {}),
      ...(filters.monitoringCategory
        ? { monitoringCategory: filters.monitoringCategory }
        : {}),
      ...(filters.alertLevel ? { alertLevel: filters.alertLevel } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.hasTickets ? { hasTickets: filters.hasTickets } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.orderNumber ? { orderNumber: filters.orderNumber } : {}),
      ...(filters.warehouseId ? { warehouseId: filters.warehouseId } : {}),
    });
    return api
      .get(`/shipments/monitoring-grid?${params.toString()}`)
      .then((res) => res.data);
  },
  getExternalSystemNames: (): Promise<string[]> =>
    api.get('/shipments/monitoring-grid/external-systems').then((res) => res.data),
  exportPackagesHistoryCsv: (filters: any = {}) => {
    const params = new URLSearchParams({
      ...(filters.externalSystemName ? { externalSystemName: filters.externalSystemName } : {}),
      ...(filters.carrier ? { carrier: filters.carrier } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.monitoringCategory
        ? { monitoringCategory: filters.monitoringCategory }
        : {}),
      ...(filters.alertLevel ? { alertLevel: filters.alertLevel } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.hasTickets ? { hasTickets: filters.hasTickets } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.warehouseId ? { warehouseId: filters.warehouseId } : {}),
    });
    return api
      .get(`/shipments/monitoring-grid/export-csv?${params.toString()}`)
      .then((res) => res.data);
  },
  getTrackingDetails: (transactionId: string) =>
    api.get(`/shipments/tracking/${transactionId}`).then((res) => res.data),
  getLabel: (shipmentId: string, trackingNumber: string) =>
    api
      .get(`/shipments/tracking/${shipmentId}/label/${trackingNumber}`)
      .then((res) => res.data),
  trackPackage: (shipmentId: string, trackingNumber: string) =>
    api
      .get(`/shipments/tracking/${shipmentId}/track/${trackingNumber}`)
      .then((res) => res.data),
  refreshTracking: (shipmentId: string, trackingNumber: string) =>
    api
      .get(`/shipments/tracking/${shipmentId}/track/${trackingNumber}/refresh`)
      .then((res) => res.data),
  requestPickup: (shipmentId: string, trackingNumber: string) =>
    api
      // POST: this books a collection with the carrier, it is not a read.
      .post(`/shipments/tracking/${shipmentId}/pickup/${trackingNumber}`)
      .then((res) => res.data),
  /**
   * Voids with the carrier and, unless voidInPlatform is false, in the order's
   * store (Magento). The response's `platform` lists what the store did per
   * tracking - see reportStoreVoid.
   */
  voidPackages: (
    packages: Array<{ shipmentId: string; trackingNumber: string }>,
    options: { voidInPlatform?: boolean } = {},
  ) =>
    api
      .post(`/shipments/void`, {
        packages,
        ...(options.voidInPlatform === undefined ? {} : { voidInPlatform: options.voidInPlatform }),
      })
      .then((res) => res.data),
  /**
   * Records-only status override - updates our DB and does NOT call the
   * carrier. For a label that still needs voiding AT the carrier, use
   * voidPackages instead.
   */
  setManualTrackingStatus: (
    trackingNumber: string,
    status: ManualTrackingStatus,
    reason?: string,
    deliveredAt?: string,
  ) =>
    api
      .post(
        `/shipments/tracking/${encodeURIComponent(trackingNumber)}/manual-status`,
        { status, reason, deliveredAt },
      )
      .then((res) => res.data),
  getBillingReport: (
    page: number = 1,
    limit: number = 10,
    filters: any = {},
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.carrier ? { carrier: filters.carrier } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.delayedBy ? { delayedBy: filters.delayedBy } : {}),
      ...(filters.source ? { source: filters.source } : {}),
      ...(filters.labelSource ? { labelSource: filters.labelSource } : {}),
      ...(filters.sku ? { sku: filters.sku } : {}),
      ...(filters.pnl ? { pnl: filters.pnl } : {}),
      ...(filters.flag ? { flag: filters.flag } : {}),
      ...(filters.tag ? { tag: filters.tag } : {}),
    });
    return api
      .get(`/shipments/billing-report?${params.toString()}`)
      .then((res) => res.data);
  },

  /** Excel workbook (Tracking + Orders sheets) as { filename, base64 }. */
  exportBillingReportXlsx: (filters: any = {}) => {
    const params = new URLSearchParams({
      ...(filters.carrier ? { carrier: filters.carrier } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.delayedBy ? { delayedBy: filters.delayedBy } : {}),
      ...(filters.source ? { source: filters.source } : {}),
      ...(filters.labelSource ? { labelSource: filters.labelSource } : {}),
      ...(filters.sku ? { sku: filters.sku } : {}),
      ...(filters.pnl ? { pnl: filters.pnl } : {}),
      ...(filters.flag ? { flag: filters.flag } : {}),
    });
    return api
      .get(`/shipments/billing-report/export-xlsx?${params.toString()}`)
      .then((res) => res.data as { filename: string; base64: string });
  },
  getTicketsForTracking: (trackingNumber: string) =>
    api
      .get(`/shipments/tracking/tickets/${encodeURIComponent(trackingNumber)}`)
      .then((res) => res.data),
  getBillingPnlChart: (filters: any = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams({
      ...(filters.carrier ? { carrier: filters.carrier } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.delayedBy ? { delayedBy: filters.delayedBy } : {}),
      ...(filters.source ? { source: filters.source } : {}),
      ...(filters.labelSource ? { labelSource: filters.labelSource } : {}),
      ...(filters.sku ? { sku: filters.sku } : {}),
      ...(filters.pnl ? { pnl: filters.pnl } : {}),
      ...(filters.flag ? { flag: filters.flag } : {}),
      ...(filters.tag ? { tag: filters.tag } : {}),
    });
    return api
      .get(`/shipments/billing-report/pnl-chart?${params.toString()}`, { signal })
      .then((res) => res.data);
  },
  /**
   * P&L Transactions page: every package for the period (dateFrom, dateTo,
   * status) with its P&L parts, newest first. `truncated` when the period
   * holds more than `cap` packages.
   */
  getBillingTransactions: (
    filters: { dateFrom?: string; dateTo?: string; status?: string; tag?: string } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams({
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
      ...(filters.tag ? { tag: filters.tag } : {}),
    });
    return api
      .get(`/shipments/billing-report/transactions?${params.toString()}`, { signal })
      .then((res) => res.data as { data: any[]; total: number; truncated: boolean; cap: number });
  },
  /** The P&L Tag filter's choices: User tags that have held a SKU. */
  /** The Billing Report's Label Source filter choices: Portal, ShipStation, external systems. */
  getBillingLabelSources: () =>
    api
      .get('/shipments/billing-report/label-sources')
      .then((res) => res.data as { value: string; label: string }[]),
  getBillingTagOptions: () =>
    api
      .get('/shipments/billing-report/tag-options')
      .then((res) => res.data as { id: string; name: string }[]),
  /** SKU typeahead for the Billing Report's SKU filter. */
  suggestBillingSkus: (q: string) =>
    api
      .get('/shipments/billing-report/sku-suggest', { params: { q } })
      .then((res) => res.data as { sku: string; name: string | null }[]),
  /** Billing Report's Landed Cost popup: one SKU's average, PO by PO. */
  getBillingLandingCost: (sku: string) =>
    api
      .get('/shipments/billing-report/landing-cost', { params: { sku } })
      .then((res) => res.data),
  getExpeditingSummary: (page: number = 1, limit: number = 20) =>
    api
      .get('/shipments/expediting/summary', { params: { page, limit } })
      .then((res) => res.data),
  getExpeditingDetail: (date: string, bucket: string) =>
    api
      .get('/shipments/expediting/detail', { params: { date, bucket } })
      .then((res) => res.data),
  /**
   * Row-level correction from the Billing Report. Send only the changed
   * fields; actualBill: null clears the bill.
   */
  /**
   * Record what was done about a Billing Report flag on one package:
   * CHALLENGED, RECOVERED (with amountRecovered), ACCEPTED, DISMISSED or OPEN.
   */
  reviewBillingFlag: (input: {
    trackingNumber: string;
    code: string;
    status: string;
    note?: string | null;
    amountRecovered?: number | null;
    amountFlagged?: number | null;
  }) => api.post('/shipments/billing-report/flags', input).then((res) => res.data),
  /** Carriers the billing import reads, with the columns each file must have. */
  getBillingCarriers: () =>
    api
      .get('/shipments/billing-report/carriers')
      .then((res) => res.data as { code: string; label: string; trackingColumn: string; amountColumn: string }[]),
  importBillingCsv: (file: File, carrier: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('carrier', carrier);
    // Override the client's default application/json - otherwise axios serializes the
    // FormData to JSON and the file never reaches the server.
    return api
      .post('/shipments/billing-report/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data as { carrier: string; total: number; matched: number; unmatched: string[]; skipped: number });
  },
  /** Re-read Amazon / eBay fees still missing on recent orders. */
  refreshMarketplaceFees: () =>
    api
      .post('/shipments/billing-report/refresh-fees')
      .then((res) => res.data as { integrations: number; pending: number; updated: number; stillMissing: number }),
  updateBillingAmounts: (
    packageId: string,
    changes: { labelCreationQuote?: number; actualBill?: number | null },
  ) =>
    api
      .post(`/shipments/billing-report/${encodeURIComponent(packageId)}/amounts`, changes)
      .then((res) => res.data),
  markBatched: (shipmentId: string) =>
    api
      .post(`/shipments/${shipmentId}/mark-batched`)
      .then((res) => res.data),
  getTransitLocations: (
    page: number = 1,
    limit: number = 20,
    filters: any = {},
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.search ? { search: filters.search } : {}),
    });
    return api
      .get(`/shipments/transit-locations?${params.toString()}`)
      .then((res) => res.data);
  },
  getTrackingEvents: (
    page: number = 1,
    limit: number = 20,
    filters: { search?: string; statuses?: string[]; dateFrom?: string; dateTo?: string } = {},
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.statuses?.length ? { status: filters.statuses.join(',') } : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    });
    return api
      .get(`/shipments/tracking-events?${params.toString()}`)
      .then((res) => res.data);
  },
  exportTransitLocationsCsv: () =>
    api
      .get('/shipments/transit-locations/export-csv')
      .then((res) => res.data),
  importTransitLocationsCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post('/shipments/transit-locations/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },
  updateCarrier: (shipmentId: string, carrierId: string) =>
    api
      .post(`/shipments/tracking/${shipmentId}/update-carrier`, { carrierId })
      .then((res) => res.data),
  updateFacility: (shipmentId: string, facilityId: string | number | null) =>
    api
      .post(`/shipments/tracking/${shipmentId}/update-facility`, { facilityId })
      .then((res) => res.data),
  updateWarehouse: (shipmentId: string, warehouseId: string | number | null) =>
    api
      .post(`/shipments/tracking/${shipmentId}/update-facility`, { facilityId: warehouseId })
      .then((res) => res.data),
};
